/*eslint-disable @typescript-eslint/no-unused-vars */
/*eslint-disable @typescript-eslint/no-explicit-any */

import { addEdge, ConnectionLineType, Controls, Edge, getNodesBounds, getViewportForBounds, ReactFlow, ReactFlowProvider, useEdgesState, useNodesState, useReactFlow } from '@xyflow/react'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { DnDProvider, useDnD } from './dndContext'
import { CustomBreadCrumbs, CustomButtonGroup, CustomDialog, DashboardLoader, useAppDispatch, useAppSelector } from '../../../globalUtils/globalExports';
import { createBreadCrumbProps, useEffectOnce } from '../../../globalUtils/globalHooks';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import './DnDFlow.css';
import PlaceholderNode from './PlaceholderNode';
import ParameterNode from './ParameterNode';
import CustomEdge from './CustomEdge';
import { ICustomTriggerCondition } from './CustomTriggerConditionComponent';
import { MarkerType } from '@xyflow/react';
import { cloneDeep, filter, isEqual, isNil } from 'lodash';
import { toast } from 'react-toastify';
import axios from 'axios';
import { toPng } from 'html-to-image';
import { fetchPreSignedURLForUpload } from '../../../rmsReduxStore/chargersRedux/chargerCreators';
import { IAdvanceRuleEngineFormRedux } from '../../../rmsReduxStore/advanceRuleEngineRedux/advanceRuleEngineType';
import { fetchAdvanceRuleEngineSetParameterParams, getAdvanceRuleEngineReducer, getReferenceDataReducer, setAdvanceRuleEngineFormRedux } from '../../../rmsReduxStore/reduxExports';
import ScheduleTimeNode from './ScheduleTimeNode';
import ThenNode from './ThenNode';
import SetParameterNode from './SetParameterNode';
import AddActionNode from './AddActionNode';
import SendNotificationNode from './SendNotification';
import RemoteCommandNode from './RemoteCommandNode';
import RaiseATicketNode from './RaiseATicketNode';
import { ICustomCondition } from './CustomConditionComponent';
import IfThenNode from './IfThenNode';
import ElseNode from './ElseNode';
import WaitNode from './WaitNode';
import NoOpNode from './NoOpNode';


export interface ExecutionPlanTrigger {
  conditions: ICustomTriggerCondition[];
  expression: string | null;
  occurrenceLogic: string | null;
  numberOfOccurrences: number | null;
  timePeriod: number | null;
  timeUnit: string | null;
  isConsecutive: boolean | null;
  waitTimePeriod: number | null;
  waitTimeUnit: string | null;
}

export interface ExecutionPlanScheduleTime {
  scheduleType: null | string,
  dateTime: null | string,
  recurrenceDays: null | string[],
  recurrenceRange: null | string,
  recurrenceCount: null | number,
  endDate: null | string,
}

export interface SetParameters {
  parameterId: string;
  parameterName?: string;
  parameterCondition: string;
  parameterConditionName?: string,
  parameterValue: string[];
  parameterType: null | string,
  parameterOperator?: string,
  connectorScope: string | null,
  connectorType: string | null,
  fieldType?: string,
  unit?: string,
  dataType?: string,
  possibleValue?: string,
  errorMessage?: null | string,
  hasOffset?: boolean
}


export interface RaiseTicketAction {
  type: string;
  // subject: string;
  nodeId: string;
  description: string;
  callType: string;
  category: string;
  subCategory: string;
  urgency: string;
}

export interface RemoteCommandAction {
  type: string;
  nodeId: string;
  remoteCommandType: string;
  url?: string;
  connectorNumber?: null | number;
  connectorScope: string | null;
  peripheralCardType: string | null;
}

export interface SendNotificationAction {
  type: string;
  nodeId: string;
  notificationType: string;
  recipients: string[];
  emailContent?: string; /// making this optional for now
  recipientsName?: string[];
}

export interface SetParameterAction {
  type: string;
  nodeId: string;
  parameters: SetParameters[];
}

export interface WaitAction {
  waitTime: number;
  waitParentNodeId: string;
  waitNodeId: string;
}

export interface NoOperationAction {
  type: string;
  nodeId: string;
}


export type ActionItem = SetParameterAction | SendNotificationAction | RemoteCommandAction | RaiseTicketAction | NoOperationAction;

export interface ExecutionPlanAction {
  flowType: string;
  list: ActionItem[];
  waitList: WaitAction[];
  parentNodeId: string;
  level: number;
}
export interface ExecutionPlanCondition {
  nodeId: string;
  type: string;
  parentNodeId: string;
  level: number;
  conditions: ICustomCondition[];
  expression: string | null;
  withinDuration: number | null;
  waitTimePeriod: number | null;
}

export interface ExecutionPlan {
  triggerType: null | string; // 'parameter' | 'scheduler'
  trigger: ExecutionPlanTrigger;
  scheduleTime: ExecutionPlanScheduleTime;
  conditionBlocks: ExecutionPlanCondition[], 
  actions: ExecutionPlanAction[];
}

export interface Nodes {
  selected: boolean;
  id: string,
  parentNodeId: string,
  level?: number,
  position: { x: number, y: number },
  type: string,
  data: any,
  flowType?:string,
  measured?: { width: number, height: number }
}

interface DnDFlowProps {
  setLevelOfHeirarchy: any,
  levelOfHeirarchy: string
}


const initialNodes: Nodes[] = [
  {
    selected: false,
    id: 'placeholder',
    parentNodeId: '-',
    type: 'placeholder',
    data: { type: 'placeholder', size: 'large' },
    position: { x: 250, y: 200 },
  },
];


const initialExecutionPlan = {
  triggerType: null,
  trigger: {
    conditions: [],
    expression: null,
    occurrenceLogic: 'basic',
    numberOfOccurrences: null,
    timePeriod: null,
    timeUnit: null,
    isConsecutive: null,
    waitTimePeriod: null,
    waitTimeUnit: null
  },
  scheduleTime: {
    scheduleType: null,
    dateTime: null,
    recurrenceDays: null,
    recurrenceRange: null,
    recurrenceCount: null,
    endDate: null
  },
  conditionBlocks: [],
  actions: []
}


const DnDFlow: React.FC<DnDFlowProps> = ({ setLevelOfHeirarchy, levelOfHeirarchy }) => {
  // list of possible node-types
  const nodeTypes = useMemo(() => (
    {
      placeholder: PlaceholderNode,
      parameter: ParameterNode,
      scheduler: ScheduleTimeNode,
      then: ThenNode,
      elseNode: ElseNode,
      actionControlSetParameter: SetParameterNode,
      addAction: AddActionNode,
      sendNotification: SendNotificationNode,
      actionControlRemoteCommand: RemoteCommandNode,
      actionControlRaiseATicket: RaiseATicketNode,
      ifThen: IfThenNode,
      ifThenElse: IfThenNode,
      wait: WaitNode,
      noOp: NoOpNode
    }
  ), []);
  const { advanceRuleEngineSetParameterParam } = useAppSelector(getReferenceDataReducer);
  const [executionPlan, setExecutionPlan] = useState<ExecutionPlan>(initialExecutionPlan);
  const initialExecutionPlanRef = useRef(initialExecutionPlan);
  const { advanceRuleEngineFormRedux } = useAppSelector(getAdvanceRuleEngineReducer);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const reactFlowInstance = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<Nodes>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const reactFlowWrapper = useRef(null);
  const [showLoader, setShowLoader] = useState(false);
  const [showDialog,setShowDialog] = useState(false);
  const [type] = useDnD();
  const { screenToFlowPosition } = useReactFlow();
  const START_X = 250;
  const START_Y = 200;
  const VERTICAL_GAP = 30;
  const PLACEHOLDER_ID = 'placeholder';
  const lastNodeIdRef = useRef<string | null>(null);
  const noOpParentIdsRef = useRef<Set<string>>(new Set());
  const imageWidth = 1028;
  const imageHeight = 800;


  const edgeTypes = { custom: CustomEdge };

  const actionItemMapping = {
    'Set Parameter': 'actionControlSetParameter',
    'Send Notification' : 'sendNotification',
    'Remote Command': 'actionControlRemoteCommand',
    'Raise Ticket' : 'actionControlRaiseATicket',
    'No Operation' : 'noOp'
  }


  const handleUpdateExecutionPlan = (data: ExecutionPlan) => {
    setExecutionPlan(data);
  }


  const onConnect = useCallback(
    (params) =>
      setEdges((eds: any[]) =>
        addEdge({ ...params, markerEnd: { type: 'arrowclosed' } }, eds)
      ),
    []
  );


  // The function layoutNodesModified and placeNodeOnCanvas are added to position nodes on canvas using DFS approach.

  const placeNodeOnCanvas = (placedNodes: any[], nodeToBePlaced:any, currY: number): {positionedNode: any, updatedCurrY: number} => {
    const parentNode = placedNodes?.find(node => node.id === nodeToBePlaced?.parentNodeId);
    let position = { x:START_X, y: currY};
    const height = nodeToBePlaced?.measured?.height ?? 80;
    const width = nodeToBePlaced?.measured?.width ?? 80;
    
    if (!parentNode) {
      const positionedNode = {
        ...nodeToBePlaced,
        position
      }
      return {positionedNode,updatedCurrY: currY + height + 15}
    }

    if(nodeToBePlaced.type === 'noOp'){
      const positionedNode = {
        ...nodeToBePlaced,
        position: {
          x: parentNode?.position?.x + 100,
          y: currY
        }
      }
      return {positionedNode,updatedCurrY: currY }
    }

    switch (nodeToBePlaced.type) {
      case 'then':
      case 'elseNode':
        position = {
          x: parentNode?.position?.x - 50,
          y: currY + VERTICAL_GAP
        }
        break;
      case 'actionControlSetParameter':
      case 'sendNotification':
      case 'actionControlRemoteCommand':
      case 'actionControlRaiseATicket':
        position = {
          x: parentNode?.position?.x + 200,
          y: (currY + VERTICAL_GAP)
        }
        break;
      case 'addAction': {
        position = {
          x: parentNode.type === 'then' || parentNode.type === 'elseNode' ? parentNode?.position?.x - 15 : parentNode?.position?.x - 10,
          y: parentNode.type === 'then' || parentNode.type === 'elseNode' ? parentNode?.position?.y + parentNode?.measured?.height - 4 : parentNode?.position?.y + parentNode?.measured?.height - 15
        }
        break;
      }
      case 'placeholder': 
        if(parentNode?.type === 'then' || parentNode?.type === 'elseNode') {
          position = {
            x: parentNode?.position?.x + 204,
            y: currY + VERTICAL_GAP
          }
        } else if(parentNode?.type === 'parameter' || parentNode?.type === 'scheduler'){
          position = {
            x: parentNode?.position?.x + ((parentNode?.measured?.width ?? 80) - width) / 2,
            y: currY + VERTICAL_GAP
          }
        }
        else {
          position = {
            x: parentNode?.position?.x + ((parentNode?.measured?.width ?? 80) + 5 - width) / 2,
            y: currY + VERTICAL_GAP
          }
        }
        break;
      case 'ifThen':
      case 'ifThenElse':
        position = {
          x: parentNode?.position?.x + 100,
          y: currY + VERTICAL_GAP + 15
        }
        break;
      default :
         position = {
          x: parentNode?.position?.x + ((parentNode?.measured?.width ?? 80) - width) / 2,
          y: currY + VERTICAL_GAP
        }
    }
    return {
      positionedNode:{
        ...nodeToBePlaced,
        position
      },
      updatedCurrY: nodeToBePlaced.type === 'addAction' ? currY : position?.y + height
    };
  }

  const layoutNodesModified = (nodes: Nodes[]) => {
    const nodesStack : any = [];
    const positionedNodes:any = [];
    let currY = START_Y;
    nodesStack.push(nodes[0]);

    while(nodesStack?.length > 0) {
      const node = nodesStack.at(-1);
      nodesStack.pop();
      
      const childrens = nodes?.filter(child => child?.parentNodeId === node.id)
      childrens.reverse();
      nodesStack.push(...childrens);

      const {positionedNode,updatedCurrY} = placeNodeOnCanvas(positionedNodes,node,currY);

      positionedNodes.push(positionedNode)

      currY = updatedCurrY
    }

    return positionedNodes;

  }

  // Adds placeholder nodes when user clicks +Add Activity.
  const addNodeToCanvasThen = (_, parentNodeId, edges, level, hierarchyLevel,flowType) => {
    const existingPlaceHolderEdge = edges?.find(edge => edge?.source === parentNodeId  && edge?.target === PLACEHOLDER_ID);
    const conditionExists = hierarchyLevel === 'disabled-then-flow' && edges?.some(edge => edge?.source === parentNodeId );
    const noOpExists = noOpParentIdsRef.current.has(parentNodeId);

    if(existingPlaceHolderEdge || conditionExists || noOpExists) return ;

    setLevelOfHeirarchy(hierarchyLevel);

    const newPlaceholder = {
      id: PLACEHOLDER_ID,
      type: 'placeholder',
      parentNodeId: parentNodeId,
      flowType,
      level : hierarchyLevel === 'action' ? level : level  +  1,
      position: {
        x: START_X,
        y: START_Y,
      },
      selected: false,
      data: { type: 'placeholder', size: 'small' },
    };

    const placeHolderEdge = {
      id: `edge-${parentNodeId}-${PLACEHOLDER_ID}`,
      source: parentNodeId,
      target: PLACEHOLDER_ID,
      type: 'custom',
      markerEnd: { type: MarkerType.ArrowClosed },
      ...(hierarchyLevel === 'action' || hierarchyLevel === 'flow-or-action' ? { sourceHandle:'right', targetHandle: 'left'} : {})
    }

    const filteredEdges = edges?.filter(e => e.target !== PLACEHOLDER_ID);
    filteredEdges.push(placeHolderEdge);

    setNodes((nds) => {
      const withoutPlaceholder = nds.filter(n => n.id !== PLACEHOLDER_ID);
      return [...withoutPlaceholder, newPlaceholder];
    });

    setEdges((eds: any) => filteredEdges);

  }

  const handleDeleteTriggerNode = () => {
    setShowDialog(true);
  }

  const handleResetExecutionPlan = () => {
    setNodes(initialNodes);
    setEdges([]);
    setExecutionPlan(initialExecutionPlan);
    initialExecutionPlanRef.current = initialExecutionPlan;
    lastNodeIdRef.current = null;
    noOpParentIdsRef.current.clear(); 
    setLevelOfHeirarchy('trigger');
    setShowDialog(false);
  }

  /*eslint-disable no-case-declarations*/
  const handleRemoveNode = (nodeId: string) => {
    const nodesTobeRemoved = new Set<string>();
    const executionPlanData = cloneDeep(executionPlan);
    setNodes(currNodes => {
      let startNode = currNodes?.find(node => node.id === nodeId);

      if(startNode?.parentNodeId?.includes('noOp')) {
        const newStartId = startNode?.parentNodeId
        startNode = currNodes?.find(node => node.id === newStartId);
      }

      if (!startNode) return currNodes;

      if (startNode.id === '2') {
        setLevelOfHeirarchy('flow');
      } else {
        if (startNode.type === 'ifThen' || startNode.type === 'ifThenElse' || startNode.type === 'wait') setLevelOfHeirarchy('disabled-then-flow');
        else setLevelOfHeirarchy('action');
      }

      const childrenMap = new Map<string, any[]>();
      currNodes.forEach(n => {
        if (!n.parentNodeId) return;
        if (!childrenMap.has(n.parentNodeId)) {
          childrenMap.set(n.parentNodeId, []);
        }
        childrenMap.get(n.parentNodeId)!.push(n);
      });

      const deletionStack = [startNode];

      while (deletionStack.length > 0) {
        const node = deletionStack.pop();
        if (!node) continue;

        nodesTobeRemoved.add(node.id);

        switch (node.type) {
          case 'ifThen':
          case 'ifThenElse':
            const conditionBlocks = executionPlanData?.conditionBlocks?.filter(conditionItem => conditionItem?.nodeId != node?.id);
            const actions = executionPlanData?.actions?.filter(actionItem => actionItem?.parentNodeId != node.id);
            executionPlanData.conditionBlocks = conditionBlocks;
            executionPlanData.actions = actions;
            break;
          case 'actionControlSetParameter':
          case 'actionControlRemoteCommand':
          case 'actionControlRaiseATicket':
          case 'sendNotification':
          case 'noOp':
            const action = executionPlanData?.actions?.find(actionItem => actionItem?.parentNodeId === node?.data?.parentNodeId && actionItem?.flowType === node?.data?.ifOrElse);
            if (action) {
              action.list = action.list?.filter(item => item?.nodeId !== node.id)
              const updatedAction = executionPlanData?.actions?.map(actionItem => {
                if (actionItem?.parentNodeId === action?.parentNodeId && actionItem?.flowType === action.flowType) return action;
                else return actionItem;
              })
              executionPlanData.actions = updatedAction;
            }
            if (node.type === 'noOp') noOpParentIdsRef.current.delete(node.parentNodeId);
            break;
          case 'wait':
            const waitAction = executionPlanData?.actions?.find(actionItem => actionItem?.parentNodeId === node?.data?.actionParentNodeId && actionItem?.flowType === node?.data?.ifOrElse);
            if (waitAction) {
              waitAction.waitList = waitAction.waitList?.filter(item => item?.waitNodeId !== node.id)
              const updatedAction = executionPlanData?.actions?.map(actionItem => {
                if (actionItem?.parentNodeId === waitAction?.parentNodeId && actionItem?.flowType === waitAction.flowType) return waitAction;
                else return actionItem;
              })
              executionPlanData.actions = updatedAction;
            }
            break;
          default:
        }
        const childrens = childrenMap.get(node.id) || [];
        deletionStack.push(...childrens);
      }
      const filteredNodes = currNodes?.filter(node => !nodesTobeRemoved.has(node.id));
      if (startNode?.id === '2') {
        const newPlaceholder = {
          id: PLACEHOLDER_ID,
          type: 'placeholder',
          parentNodeId: '1',
          position: {
            x: 250,
            y: 200,
          },
          selected: false,
          data: { type: 'placeholder', size: 'small' },
        };
        filteredNodes.push(newPlaceholder);
      }
      lastNodeIdRef.current = String((filteredNodes ?? [])?.filter(node => node.type !== 'addAction' && node.id !== PLACEHOLDER_ID && node.type !== 'noOp')?.reduce((max, node) => Math.max(max, Number(node.id)), 0));
      return layoutNodesModified(filteredNodes);
    });
    setEdges((currEdges) => {
      const filteredEdges = currEdges?.filter(edge => !(nodesTobeRemoved.has(edge?.target) || nodesTobeRemoved.has(edge?.source)));
      if (nodesTobeRemoved.has('2')) {
        const placeHolderEdge = {
          id: `edge-${'1'}-${PLACEHOLDER_ID}`,
          source: '1',
          target: PLACEHOLDER_ID,
          type: 'custom',
          markerEnd: { type: MarkerType.ArrowClosed },
        }
        filteredEdges.push(placeHolderEdge);
      }
      return filteredEdges;
    })
    handleUpdateExecutionPlan(executionPlanData);
  }

  const checkDuplicate = (nodeType:string, parentNodeId) => {
    const nodeExists = nodes.some(node => node.type === nodeType && node?.parentNodeId === parentNodeId);
    return nodeExists;
  }

  const onNodesChangeCustom = useCallback(
    (changes) => {
      onNodesChange(changes);

      const hasDimensionChange = changes.some(
        (c) => c.type === 'dimensions'
      );

      if (hasDimensionChange) {
        setNodes((nds) => layoutNodesModified([...nds]));
      }
    },
    [onNodesChange]
  );

  const isInsidePlaceholder = (
    dropPos: { x: number; y: number },
    placeholder: Nodes
  ) => {
    if (!placeholder.measured) return false;

    const { x, y } = placeholder.position;
    const { width, height } = placeholder.measured;

    return (
      dropPos.x >= x &&
      dropPos.x <= x + width &&
      dropPos.y >= y &&
      dropPos.y <= y + height
    );
  };

  // This function is used to create nodes and edges on the Canvas when someone drops node from sidebar.
  const onDrop = useCallback((event) => {

    event.preventDefault();
    if (!type)return;


    const position = screenToFlowPosition(
      {
        x: event.clientX,
        y: event.clientY
      }
    )

    const placeholderNode = nodes.find((n) => n.id === PLACEHOLDER_ID);

    if (!placeholderNode || !isInsidePlaceholder(position, placeholderNode)) {
      return;
    }

    // let newNode: any = null;
    // let newPlaceholder: any = null;
    const nodesToBeAdded: any[] = [];
    const filteredEdges = edges?.filter(e => e.target !== PLACEHOLDER_ID);
    const nodeId = lastNodeIdRef?.current ? `${Number(lastNodeIdRef?.current) + 1}` : '1';
    let parentNodeId = placeholderNode?.parentNodeId;
    const executionPlanData = cloneDeep(executionPlan);

    //This if condition is added to add no operation action in case of if added just after else to ensure elseif functionality
    if(type === 'ifThen' || type === 'ifThenElse') {
      const parentNode = nodes.find(item => item?.id === parentNodeId);
      if(parentNode?.type === 'elseNode') {
        noOpParentIdsRef.current.add(parentNodeId); 
        const newNoOpNode = {
          id: `${parentNodeId}-noOp`,
          parentNodeId,
          level: (placeholderNode?.level ?? 1) - 1,
          type: 'noOp',
          position: {
            x: placeholderNode.position.x,
            y: placeholderNode.position.y,
          },
          data: {
            ifOrElse: placeholderNode?.flowType,
            nodeId: `${parentNodeId}-noOp`,
            level: placeholderNode?.level,
            parentNodeId:  placeholderNode?.flowType === 'If' ? `${Number(parentNodeId) - 1}` : `${Number(parentNodeId) - 2}`
          },
        }

        const noOpAction: NoOperationAction =  {
          nodeId: newNoOpNode?.id,
          type: 'No Operation'
        }

        const actionIdx = executionPlanData?.actions?.findIndex((executionDataItem) => executionDataItem.flowType === newNoOpNode?.data?.ifOrElse && executionDataItem?.parentNodeId === newNoOpNode?.data?.parentNodeId);
        const actionItemIndex = executionPlanData?.actions?.[actionIdx]?.list?.findIndex(item => item?.type === 'No Operation');

        if(actionItemIndex === -1){
            executionPlanData?.actions?.[actionIdx]?.list?.push(noOpAction)
        } else {
            executionPlanData.actions[actionIdx].list[actionItemIndex] = noOpAction
        }

        nodesToBeAdded.push(newNoOpNode);
        parentNodeId = newNoOpNode?.id
      }
    }

    if (type === 'parameter' || type === 'scheduler') {
      executionPlanData.triggerType = type;
      handleUpdateExecutionPlan(executionPlanData);

      setLevelOfHeirarchy('flow');

      const newNode = {
        id: nodeId,
        type,
        parentNodeId,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {
          label: `${type} node`,
          executionPlan: executionPlanData,
          handleUpdateExecutionPlan: handleUpdateExecutionPlan,
          removeNode: handleDeleteTriggerNode
        },
      };

      const newPlaceholder = {
        id: PLACEHOLDER_ID,
        type: 'placeholder',
        parentNodeId: nodeId,
        position: {
          x: placeholderNode.position.x + 250,
          y: placeholderNode.position.y,
        },
        selected: false,
        data: { type: 'placeholder', size: 'small' },
      };

      const placeHolderEdge = {
        id: `edge-${newNode?.id}-${PLACEHOLDER_ID}`,
        source: newNode?.id,
        target: PLACEHOLDER_ID,
        type: 'custom',
        markerEnd: { type: MarkerType.ArrowClosed },
      }

      filteredEdges?.push(placeHolderEdge);

      nodesToBeAdded?.push(newNode,newPlaceholder);
      lastNodeIdRef.current = nodeId;
    }
    else if (type === 'ifThen') {
      setLevelOfHeirarchy('action');
      const newIfNode = {
        id: nodeId,
        type,
        parentNodeId,
        level: placeholderNode?.level  ? placeholderNode?.level : 0,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {
          label: `${type} node`,
          executionPlan: executionPlanData,
          handleUpdateExecutionPlan: handleUpdateExecutionPlan,
          removeNode: handleRemoveNode,
          parentNodeId,
          level: placeholderNode?.level ? placeholderNode?.level : 0,
          nodeId,
          type
        }
      }
      const newThenNode = {
        id: `${Number(newIfNode?.id)+1}`,
        type: 'then',
        parentNodeId: newIfNode?.id,
        level: newIfNode?.level,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {},
      }

      const newThenActivity = {
        id: `${newThenNode?.id}-b`,
        type: 'addAction',
        parentNodeId: newThenNode?.id,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {
          addNodeToCanvas: addNodeToCanvasThen,
          id: `${newThenNode?.id}-b`,
          idToConnect: newThenNode?.id,
          edges,
          level: newThenNode?.level,
          hierarchyLevel: 'action',
          flowType: 'If'
        },
      };

      if(parentNodeId) {
        const isElseIfEdge = parentNodeId != placeholderNode?.parentNodeId;
        const newEdge: Edge = {
          id: `edge-${placeholderNode?.parentNodeId}-${nodeId}`,
          source: placeholderNode?.parentNodeId,
          target: nodeId,
          type: 'custom',
          markerEnd: { type: MarkerType.ArrowClosed },
          ...(isElseIfEdge ? {sourceHandle: 'right'} : {})
        }
        filteredEdges?.push(newEdge);
      }

      const ifThenEdge: Edge = {
        id: `edge-${nodeId}-${newThenNode?.id}`,
        source: nodeId,
        target: newThenNode?.id,
        type: 'custom',
        markerEnd: { type: MarkerType.ArrowClosed },
        sourceHandle: 'left'
      }


      filteredEdges?.push(ifThenEdge);

      nodesToBeAdded?.push(newIfNode,newThenNode,newThenActivity);

      lastNodeIdRef.current = newThenNode?.id;
      const action = {
        flowType: 'If',
        list: [],
        waitList: [],
        parentNodeId: nodeId,
        level: newIfNode?.level
      }
      executionPlanData?.actions?.push(action);

      handleUpdateExecutionPlan(executionPlanData);
    }
    else if (type === 'ifThenElse') {
      setLevelOfHeirarchy('action');
      const newIfNode = {
        id: nodeId,
        type,
        parentNodeId,
        level: placeholderNode?.level ? placeholderNode?.level : 0,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {
          label: `${type} node`,
          executionPlan: executionPlanData,
          handleUpdateExecutionPlan: handleUpdateExecutionPlan,
          removeNode: handleRemoveNode,
          parentNodeId,
          level: placeholderNode?.level ? placeholderNode?.level : 0,
          nodeId,
          type
        }
      }
      const newThenNode = {
        id: `${Number(newIfNode?.id)+1}`,
        type: 'then',
        parentNodeId: newIfNode?.id,
        level: newIfNode?.level,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {},
      }

      const newElseNode = {
        id: `${Number(newIfNode?.id)+2}`,
        type: 'elseNode',
        parentNodeId: newIfNode?.id,
        level: newIfNode?.level,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {},
      }

      const newThenActivity = {
        id: `${newThenNode?.id}-b`,
        type: 'addAction',
        parentNodeId: newThenNode?.id,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {
          addNodeToCanvas: addNodeToCanvasThen,
          id: `${newThenNode?.id}-b`,
          idToConnect: newThenNode?.id,
          edges,
          level: newThenNode?.level,
          hierarchyLevel:'action',
          flowType: 'If'
        },
      };

      const newElseActivity = {
        id: `${newElseNode?.id}-b`,
        type: 'addAction',
        parentNodeId: newElseNode?.id,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {
          addNodeToCanvas: addNodeToCanvasThen,
          id: `${newElseNode?.id}-b`,
          idToConnect: newElseNode?.id,
          edges,
          level: newElseNode?.level,
          hierarchyLevel:'flow-or-action',
          flowType: 'Else'
        },
      };

      nodesToBeAdded.push(newIfNode,newThenNode,newElseNode,newThenActivity,newElseActivity);

      if(parentNodeId) {
        const isElseIfEdge = parentNodeId != placeholderNode?.parentNodeId;
        const newEdge: Edge = {
          id: `edge-${placeholderNode?.parentNodeId}-${nodeId}`,
          source: placeholderNode?.parentNodeId,
          target: nodeId,
          type: 'custom',
          markerEnd: { type: MarkerType.ArrowClosed },
          ...(isElseIfEdge ? {sourceHandle: 'right'} : {})
        }
        filteredEdges?.push(newEdge);
      }


      const ifThenEdge: Edge = {
        id: `edge-${nodeId}-${newThenNode?.id}`,
        source: nodeId,
        target: newThenNode?.id,
        type: 'custom',
        markerEnd: { type: MarkerType.ArrowClosed },
        sourceHandle: 'left'
      }

      const ifElseEdge: Edge = {
        id: `edge-${nodeId}-${newElseNode?.id}`,
        source: nodeId,
        target: newElseNode?.id,
        type: 'custom',
        markerEnd: { type: MarkerType.ArrowClosed },
        sourceHandle: 'left'
      }

      filteredEdges?.push(ifThenEdge,ifElseEdge);
      const ifAction = {
        flowType: 'If',
        list: [],
        waitList:[],
        parentNodeId: newIfNode?.id,
        level: newIfNode?.level,
      }
      const elseAction = {
        flowType: 'Else',
        list: [],
        waitList:[],
        parentNodeId: newIfNode?.id,
        level: newIfNode?.level
      }
      executionPlanData?.actions?.push(ifAction,elseAction);
      handleUpdateExecutionPlan(executionPlanData);
      lastNodeIdRef.current = newElseNode?.id;


    }
    else if (type === 'then') {
      if(parentNodeId !== '1')return;
      setLevelOfHeirarchy('action');
      const newNode = {
        id: nodeId,
        type,
        parentNodeId,
        level: placeholderNode?.level ?? 0 ,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {},
      };

      const newThenActivity = {
        id: `${newNode?.id}-b`,
        type: 'addAction',
        parentNodeId: newNode?.id,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {
          addNodeToCanvas: addNodeToCanvasThen,
          id: `${newNode?.id}-b`,
          idToConnect: newNode?.id,
          edges,
          level: newNode?.level,
          hierarchyLevel:'action',
          flowType: 'If'
        },
      };

      if (parentNodeId) {
        const newEdge: Edge = {
          id: `edge-${parentNodeId}-${nodeId}`,
          source: parentNodeId,
          target: nodeId,
          type: 'custom',
          markerEnd: { type: MarkerType.ArrowClosed },
          sourceHandle: 'left'
        }
        filteredEdges?.push(newEdge);
      }

      // Add condition flow
      const action = {
        flowType: 'If',
        list: [],
        waitList:[],
        parentNodeId: parentNodeId,
        level: newNode?.level
      }
      executionPlanData?.actions?.push(action);
      handleUpdateExecutionPlan(executionPlanData);
      lastNodeIdRef.current = nodeId;
      nodesToBeAdded?.push(newNode,newThenActivity);
    }

    else if (type === 'actionControlSetParameter' || type === 'sendNotification' || type === 'actionControlRemoteCommand' || type === 'actionControlRaiseATicket') {
      if(checkDuplicate(type,placeholderNode?.parentNodeId)) { 
        toast.error('Cannot have duplicate controls in a single flow.');
        return;
      }
      const newNode = {
        id: nodeId,
        parentNodeId,
        level: placeholderNode?.level,
        type,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {
          actionButtonVisible: '',
          addNodeToCanvas: undefined,
          addIcon: false,
          executionPlan: executionPlanData,
          handleUpdateExecutionPlan: handleUpdateExecutionPlan,
          ifOrElse: placeholderNode?.flowType,
          removeNode: handleRemoveNode,
          nodeId: nodeId,
          typeOfNode: type,
          level: placeholderNode?.level,
          parentNodeId:  placeholderNode?.flowType === 'If' ? `${Number(parentNodeId) - 1}` : `${Number(parentNodeId) - 2}`
        },
      };

      const newAddActivity = {
        id: `${newNode?.id}-b`,
        type: 'addAction',
        parentNodeId: nodeId,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {
          addNodeToCanvas: addNodeToCanvasThen,
          id: `${newNode?.id}-b`,
          idToConnect: newNode?.id,
          edges,
          level: newNode?.level,
          hierarchyLevel: 'disabled-then-flow',
          flowType: placeholderNode?.flowType
        },
      };

      if (parentNodeId) {
        const newEdge: Edge = {
          id: `edge-${parentNodeId}-${newNode?.id}`,
          source: parentNodeId,
          target: nodeId,
          type: 'custom',
          markerEnd: { type: MarkerType.ArrowClosed },
          sourceHandle: 'right',
          targetHandle: 'left'
        }
        filteredEdges?.push(newEdge);
      }

      lastNodeIdRef.current = nodeId;
      nodesToBeAdded?.push(newNode,newAddActivity);
    }

    else if (type === 'wait') {
      const parentNode = nodes?.find(node => node.id === parentNodeId);
      const newNode : any = {
        id: nodeId,
        parentNodeId,
        level: (placeholderNode?.level ?? 1)  - 1,
        type,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data:{
          executionPlan: executionPlanData,
          handleUpdateExecutionPlan: handleUpdateExecutionPlan,
          ifOrElse: placeholderNode?.flowType,
          removeNode: handleRemoveNode,
          actionParentNodeId:  placeholderNode?.flowType === 'If' ? `${Number(parentNode?.parentNodeId) - 1}` : `${Number(parentNode?.parentNodeId) - 2}`,
          parentNodeId: parentNodeId,
          nodeId
        }
      }
      const newActivityNode : any = {
        id: `${newNode?.id}-b`,
        type: 'addAction',
        parentNodeId: nodeId,
        position: {
          x: placeholderNode.position.x,
          y: placeholderNode.position.y,
        },
        data: {
          addNodeToCanvas: addNodeToCanvasThen,
          id: `${newNode?.id}-b`,
          idToConnect: newNode?.id,
          edges,
          level: newNode?.level,
          hierarchyLevel: 'disabled-then-flow'
        },
      }
      
      if (parentNodeId) {
        const newEdge: Edge = {
          id: `edge-${parentNodeId}-${newNode?.id}`,
          source: parentNodeId,
          target: nodeId,
          type: 'custom',
          markerEnd: { type: MarkerType.ArrowClosed },
        }
        filteredEdges?.push(newEdge);
      }

      lastNodeIdRef.current = nodeId;
      nodesToBeAdded.push(newNode,newActivityNode);
    }

    setNodes((nds) => {
      const withoutPlaceholder = nds.filter(n => n.id !== PLACEHOLDER_ID);
      return [...withoutPlaceholder, ...nodesToBeAdded];
    });

    setEdges((eds: any) => filteredEdges);

  }, [type, screenToFlowPosition, nodes])


  // Default React-flow function.
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  //To remove the react-flow watermark.
  const proOptions = { hideAttribution: true };

  // ToDo: Added a function to autoPopulate missing fields in data for nodes 

  /*eslint-disable no-case-declarations*/
  const autoPopulateRemainingFields = async() => {
    if(!advanceRuleEngineFormRedux?.executionPlan)return;
    const existingExecutionPlan = cloneDeep(advanceRuleEngineFormRedux?.executionPlan);
    const ruleEngineSetParameterParamRef = advanceRuleEngineSetParameterParam ? advanceRuleEngineSetParameterParam : JSON.parse(await dispatch(fetchAdvanceRuleEngineSetParameterParams()));

    const setParamConditionNameMap = {
      'is':'is',
      'equalsTo': 'Equals to',
      '+':'+ (plus)',
      '-': '- (minus)'
    }

    const updatedActions = existingExecutionPlan?.actions?.map(action => {
      const updatedActionList = action?.list?.map(actionItem => {
        switch (actionItem?.type) {
           case 'Set Parameter':
            const updatedParameters = actionItem?.parameters?.map((param,idx)=>{
              const parameterRef = ruleEngineSetParameterParamRef?.find(item=> item?.id === param?.parameterId);
              const updatedParam = {
                ...param,
                parameterName: parameterRef.label,
                parameterConditionName: setParamConditionNameMap[param?.parameterCondition],
                parameterType: parameterRef?.parameterType,
                parameterOperator: idx > 0 ? 'AND' : null,
                fieldType: parameterRef?.fieldType,
                dataType: parameterRef?.dataType,
                unit: parameterRef?.unit,
                possibleValue: parameterRef?.possibleValue,
                errorMessage: null,
                connectorScope: param?.connectorScope != undefined ? param?.connectorScope : parameterRef?.parameterType === 'ConnectorParameter' ? 'any' : null,
                connectorType: param?.connectorType != undefined ? param?.connectorType : parameterRef?.acOrDc
              }
              return updatedParam;
            });

            return {
             ...actionItem,
             parameters: updatedParameters 
            }
          default:
            return actionItem;
        }
      })
      return {
        ...action,
        list: updatedActionList
      }
    });

    existingExecutionPlan.actions = updatedActions;
  
    initialExecutionPlanRef.current = existingExecutionPlan;
    handleUpdateExecutionPlan(existingExecutionPlan);
    recreateFlowFromExecutionPlan(existingExecutionPlan);
  }


  // This function is used to recreate flow diagram from execution plan when user creates edit button.
  const recreateFlowFromExecutionPlan = (executionPlan) => {
    if (!executionPlan) return;
    const existingExecutionPlan = cloneDeep(executionPlan);
    const newNodes: any = [];
    const newEdges: any = [];
    const isCondtion: Map<string,boolean> = new Map();

    if (existingExecutionPlan?.triggerType === 'parameter') {
      const newNode: any = {
        id: '1',
        type: 'parameter',
        parentNodeId:'-',
        position: {
          x: 250,
          y: 200,
        },
        data: {
          label: 'parameter node',
          executionPlan: existingExecutionPlan,
          handleUpdateExecutionPlan: handleUpdateExecutionPlan,
          removeNode: handleDeleteTriggerNode
        },
      };
      newNodes.push(newNode);
    }
    if (existingExecutionPlan?.triggerType === 'scheduler') {
      const newNode: any = {
        id: '1',
        type: 'scheduler',
        parentNodeId:'-',
        position: {
          x: 250,
          y: 200,
        },
        data: {
          label: 'scheduler node',
          executionPlan: existingExecutionPlan,
          handleUpdateExecutionPlan: handleUpdateExecutionPlan,
          removeNode: handleDeleteTriggerNode
        },
      };
      newNodes.push(newNode);
    }

    existingExecutionPlan?.conditionBlocks?.forEach(executionCondition => {
      const newIfNode = {
        id: executionCondition?.nodeId,
        type: executionCondition?.type,
        parentNodeId: executionCondition?.parentNodeId,
        level: executionCondition?.level,
        position: {
          x: 250,
          y: 200,
        },
        data: {
          label: `${executionCondition?.type} node`,
          executionPlan: existingExecutionPlan,
          handleUpdateExecutionPlan: handleUpdateExecutionPlan,
          removeNode: handleRemoveNode,
          parentNodeId: executionCondition?.parentNodeId,
          level: executionCondition?.level,
          nodeId: executionCondition?.nodeId,
          type: executionCondition?.type,
        }
      }

      const newThenNode = {
        id: `${Number(newIfNode?.id)+1}`,
        type: 'then',
        parentNodeId: newIfNode?.id,
        level: newIfNode?.level,
        position: {
          x: 250,
          y: 200,
        },
        data: {},
      }

      const newThenActivity = {
        id: `${newThenNode?.id}-b`,
        type: 'addAction',
        parentNodeId: newThenNode?.id,
        position: {
          x: 250,
          y: 200,
        },
        data: {
          addNodeToCanvas: addNodeToCanvasThen,
          id: `${newThenNode?.id}-b`,
          idToConnect: newThenNode?.id,
          edges,
          level: newThenNode?.level,
          hierarchyLevel: 'action',
          flowType: 'If'
        },
      };

      newNodes.push(newIfNode,newThenNode,newThenActivity);

      if( newIfNode?.type === 'ifThenElse' ){
        const newElseNode = {
          id: `${Number(newIfNode?.id)+2}`,
          type: 'elseNode',
          parentNodeId: newIfNode?.id,
          level: newIfNode?.level,
          position: {
            x: 250,
            y: 200,
          },
          data: {},
        }

        const newElseActivity = {
          id: `${newElseNode?.id}-b`,
          type: 'addAction',
          parentNodeId: newElseNode?.id,
          position: {
            x: 250,
            y: 200,
          },
          data: {
            addNodeToCanvas: addNodeToCanvasThen,
            id: `${newElseNode?.id}-b`,
            idToConnect: newElseNode?.id,
            edges,
            level: newElseNode?.level,
            hierarchyLevel: 'flow-or-action',
            flowType: 'Else'
          },
        }

        newNodes?.push(newElseNode,newElseActivity);
      }

      isCondtion.set(newIfNode?.id, true);

    })
    
    existingExecutionPlan?.actions?.forEach(action => {
      if(!action?.parentNodeId)return;
      if(action?.flowType === 'If' && !isCondtion.get(action?.parentNodeId)){
        const parentNodeId = action?.parentNodeId;
        const newThenNode: any = {
          id:  `${Number(parentNodeId)+1}`,
          type: 'then',
          parentNodeId,
          level: action?.level,
          position: {
            x: 250,
            y: 200
          },
          data: {},
        }

        const newThenActivity = {
          id: `${newThenNode?.id}-b`,
          type: 'addAction',
          parentNodeId: newThenNode?.id,
          position: {
            x: 250,
            y: 200,
          },
          data: {
            addNodeToCanvas: addNodeToCanvasThen,
            id: `${newThenNode?.id}-b`,
            idToConnect: newThenNode?.id,
            edges,
            level: newThenNode?.level,
            hierarchyLevel: 'action',
            flowType: 'If'
          },
        };
        newNodes.push(newThenNode,newThenActivity);
      }
      const actionParentId =  action?.flowType === 'If' ? `${Number(action?.parentNodeId) + 1}` : `${Number(action?.parentNodeId) + 2}`;
      action?.list?.forEach(actionItem => {
        if(!actionItem?.nodeId)return;
        if(actionItem?.type === 'No Operation') {
          const newNoOpNode = {
            id: actionItem?.nodeId,
            parentNodeId: actionParentId,
            level: action?.level,
            type: 'noOp',
            position: {
              x: 250,
              y: 250,
            },
            data: {
              ifOrElse: action?.flowType,
              nodeId: actionItem?.nodeId,
              level: action?.level,
              parentNodeId:  action?.parentNodeId
            },
          }
          newNodes.push(newNoOpNode);
        }
        else {
          const actionNode = {
            id: actionItem?.nodeId,
            parentNodeId: actionParentId,
            level: action?.level,
            type: actionItemMapping?.[actionItem?.type],
            position: {
              x: 250,
              y: 250
            },
            data: {
              actionButtonVisible: '',
              addNodeToCanvas: undefined,
              addIcon: false,
              executionPlan: existingExecutionPlan,
              handleUpdateExecutionPlan: handleUpdateExecutionPlan,
              ifOrElse: action?.flowType,
              removeNode: handleRemoveNode,
              nodeId: actionItem?.nodeId,
              typeOfNode: actionItemMapping?.[actionItem?.type],
              level: action?.level,
              parentNodeId: action?.parentNodeId
            },
          }
          const addActivityNode = {
            id: `${actionNode?.id}-b`,
            type: 'addAction',
            parentNodeId: actionNode?.id,
            position: {
              x: 250,
              y: 200,
            },
            data: {
              addNodeToCanvas: addNodeToCanvasThen,
              id: `${actionNode?.id}-b`,
              idToConnect: actionNode?.id,
              edges,
              hierarchyLevel: 'disabled-then-flow',
              level: action?.level,
              flowType: action?.flowType
            },
          }
          newNodes.push(actionNode,addActivityNode);
        }
      });

      action?.waitList?.forEach(waitItem => {
        if(!waitItem?.waitNodeId)return;
        const waitNode = {
            id: waitItem?.waitNodeId,
            parentNodeId: waitItem?.waitParentNodeId,
            level: action?.level,
            type: 'wait',
            position: {
              x: 250,
              y: 250
            },
            data: {
              executionPlan: existingExecutionPlan,
              handleUpdateExecutionPlan: handleUpdateExecutionPlan,
              ifOrElse: action?.flowType,
              removeNode: handleRemoveNode,
              nodeId: waitItem?.waitNodeId,
              parentNodeId: waitItem?.waitParentNodeId,
              actionParentNodeId: action?.parentNodeId
            },
        }
        const addActivityNode = {
          id: `${waitNode?.id}-b`,
          type: 'addAction',
          parentNodeId: waitNode?.id,
          position: {
            x: 250,
            y: 200,
          },
          data: {
            addNodeToCanvas: addNodeToCanvasThen,
            id: `${waitNode?.id}-b`,
            idToConnect: waitNode?.id,
            edges,
            level: waitNode?.level,
            hierarchyLevel: 'disabled-then-flow'
          },
        }
        newNodes.push(waitNode,addActivityNode);
      })
    })

    // This may not happen in real situation since a rule without action wont be saved
    // For now no such check is implemented thats why adding this.
    if(newNodes?.length === 1) {
      const placeHolderNode = {
        id: PLACEHOLDER_ID,
        type: 'placeholder',
        parentNodeId: '1',
        level: 0,
        position: {
          x: 250,
          y: 200
        },
        selected: false,
        data: { type: 'placeholder', size: 'small' },
      }
      newNodes.push(placeHolderNode)
    }


    newNodes.forEach(node =>  {
      let newEdge:any = null;
      if(node.parentNodeId === '-' || node.type === 'addAction')return;
      switch(node.type) {
        case 'then':
        case 'elseNode':
          newEdge = {
          id: `edge-${node.parentNodeId}-${node.id}`,
          source: node.parentNodeId,
          target: node.id,
          type: 'custom',
          markerEnd: { type: MarkerType.ArrowClosed },
          sourceHandle:'left',
        }
        break;
        case 'actionControlSetParameter':
        case 'actionControlRemoteCommand':
        case 'actionControlRaiseATicket':
        case 'sendNotification':
          newEdge = {
          id: `edge-${node.parentNodeId}-${node.id}`,
          source: node.parentNodeId,
          target: node.id,
          type: 'custom',
          markerEnd: { type: MarkerType.ArrowClosed },
          sourceHandle:'right',
          targetHandle: 'left'
        }
        break;
        case 'ifThen':
        case 'ifThenElse':
          const [effectiveParentNodeId,noOp] = node?.parentNodeId ? node.parentNodeId.split('-') : ['0',null];
          newEdge = {
            id: `edge-${effectiveParentNodeId}-${node.id}`,
            source: effectiveParentNodeId,
            target: node.id,
            type: 'custom',
            markerEnd: { type: MarkerType.ArrowClosed },
            ...(!isNil(noOp) ? {sourceHandle: 'right'} : {})
          }
          break;
        default:
        newEdge = {
          id: `edge-${node.parentNodeId}-${node.id}`,
          source: node.parentNodeId,
          target: node.id,
          type: 'custom',
          markerEnd: { type: MarkerType.ArrowClosed },
        }
      }
      newEdges.push(newEdge);
    });
    lastNodeIdRef.current = String((newNodes ?? [])?.filter(node => node.type !== 'addAction' && node.id !== PLACEHOLDER_ID && node.type !== 'noOp')?.reduce((max,node)=>Math.max(max,Number(node.id)),0));
    setNodes(newNodes);
    // Edges can be created only when nodes exist thus using requestAnimationFrame to wait for the DOM to "paint" the new nodes
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setEdges(newEdges);
        // Note: This will be removed in future allowing user to add both flow type and action.
        if(newNodes?.some(node => node.type === 'then'))setLevelOfHeirarchy('action');
        else setLevelOfHeirarchy('flow');
      });
    });
  }

  useLayoutEffect(() => {
    autoPopulateRemainingFields();
  },[]);

  //STOPPING THE REFRESH IN THE EXECUTION PLAN
  useEffect(()=>{
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ''; // Required for Chrome
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  },[]);

  //This useEffect updates the ExecutionPlan for the already created nodes and provided updated set of edges to +Add Activity nodes.
  useEffect(() => {
    setNodes((prevNodes) => {
      // Process nodes once
      return prevNodes.map((node) => {
        // Clone the node for modifications
        let updatedNode = { ...node };

        // Condition 1: Update execution plan data for specific nodes
        if (node.type === 'parameter' || node.type === 'scheduler') {
          updatedNode = {
            ...updatedNode,
            data: {
              ...updatedNode.data,
              executionPlan,
              handleUpdateExecutionPlan,
              removeNode: handleDeleteTriggerNode
            },
          };
        }

        if ( node.type === 'actionControlSetParameter' || node.type === 'sendNotification' || node.type === 'actionControlRemoteCommand' || node.type === 'actionControlRaiseATicket' || node.type === 'ifThen' || node.type === 'ifThenElse' || node.type === 'wait') {
          updatedNode = {
            ...updatedNode,
            data: {
              ...updatedNode.data,
              executionPlan,
              handleUpdateExecutionPlan,
              removeNode: handleRemoveNode
            },
          };
        }

        if (node.type === 'addAction') {
          updatedNode = {
            ...updatedNode,
            data: {
              ...updatedNode.data,
              edges
            },
          };
        }

        return updatedNode;
      });
    });

  }, [executionPlan,edges]);

  const handleFileChange = async (file: File | null): Promise<void> => {
    if (file && file?.name) {
      const responsePreSigned = await dispatch(fetchPreSignedURLForUpload({ fileName: file?.name }));
      if (responsePreSigned && responsePreSigned.data != null) {
        const responsePut = await axios.put(responsePreSigned?.data?.url, file, {
          headers: {
            'Content-Type': file.type,
            'x-ms-blob-type': 'BlockBlob'
          }
        }
        )
        if (responsePut?.status === 201 || responsePut?.status === 200 || responsePut?.status === 202) {
          const ruleEngineForm: IAdvanceRuleEngineFormRedux = {
            id: advanceRuleEngineFormRedux?.id ?? '',
            name: advanceRuleEngineFormRedux?.name ?? '',
            description: advanceRuleEngineFormRedux?.description ?? '',
            chargerIds: advanceRuleEngineFormRedux?.chargerIds ?? null,
            status: advanceRuleEngineFormRedux?.status ?? '',
            draftStatus: advanceRuleEngineFormRedux?.draftStatus ?? '',
            executionPlan: executionPlan,
            fileName: responsePreSigned?.data?.blobName,
            imageURL: responsePreSigned?.data?.url,
            autoAssign: advanceRuleEngineFormRedux?.autoAssign ?? '',
            runningStatus: advanceRuleEngineFormRedux?.runningStatus,
            executionThreshold: advanceRuleEngineFormRedux?.executionThreshold,
            resetTimePeriod: advanceRuleEngineFormRedux?.resetTimePeriod,
            resetTimeUnit: advanceRuleEngineFormRedux?.resetTimeUnit,
            autoAssignCustomerIds: advanceRuleEngineFormRedux?.autoAssignCustomerIds,
            autoAssignProductTypeIds: advanceRuleEngineFormRedux?.autoAssignProductTypeIds
          };

          dispatch(setAdvanceRuleEngineFormRedux(ruleEngineForm));
          setShowLoader(false)
          navigate('/new-rule-advance', { state: { mode: location?.state?.mode, ruleId: location?.state?.ruleId ?? advanceRuleEngineFormRedux?.id } });
        }

      }
    }
  };


  const dataURLtoBlob = (dataUrl: string): Blob => {
    const byteString = atob(dataUrl.split(',')[1]);
    const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uintArray = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uintArray[i] = byteString.charCodeAt(i);
    }

    return new Blob([uintArray], { type: mimeString });
  };

  // This function is written to check if rule created by user is valid or not. It makes following checks:
  // 1. Trigger block should not be empty.
  // 2. Atleast 1 action should be there.
  // 3. No added node should be empty.
  const checkValidRule = () => {
    const flatActionList = new Set(executionPlan?.actions?.flatMap(action => action.list ?? [])?.map(action => action.nodeId));
    const flatWaitList = new Set(executionPlan?.actions?.flatMap(action=>action.waitList ?? [])?.map(action => action.waitNodeId));
    const flatConditionList = new Set(executionPlan?.conditionBlocks?.map(condition => condition?.nodeId));
    const conditionsWithoutActions = new Set();

    executionPlan?.actions?.forEach(action => {
      if(action.list?.length === 0){
        conditionsWithoutActions?.add(action.parentNodeId);
      }
    });

    // 2. Atleast 1 action should be there.
    if(flatActionList.size === 0)return false;

    const filteredNodes = nodes.filter(node => node.type!=='then' && node.type!=='elseNode' && node.type!=='addAction' && node.type !== 'placeholder');

    return !filteredNodes.some(node => {
      switch(node.type){
        case 'parameter':
          return !executionPlan?.trigger?.expression?.trim() || 
            (executionPlan?.trigger?.occurrenceLogic === 'advance' && ( !executionPlan?.trigger?.numberOfOccurrences || ( !executionPlan?.trigger?.isConsecutive && (!executionPlan?.trigger?.timePeriod || !executionPlan?.trigger?.timeUnit)))) && ( !executionPlan?.trigger?.waitTimePeriod || !executionPlan?.trigger?.waitTimeUnit );
        case 'scheduler':
          return !executionPlan?.scheduleTime?.scheduleType;
        case 'ifThen':
        case 'ifThenElse':
          return conditionsWithoutActions?.has(node.id) || !flatConditionList.has(node.id);
        default:
          return !flatActionList.has(node.id) && !flatWaitList.has(node.id);
      }
    });
  }

  const disableButton = () => {
    if(!checkValidRule())return true;
    return isEqual(executionPlan, initialExecutionPlanRef?.current);
  }

  const btnsList = [
    {
      buttonText: 'Save',
      buttonId: 'add',
      btnClassName: disableButton() ? 'primary__btn disabled' : 'primary__btn',
      isDisabled: disableButton(),
      handleClick: async (): Promise<any> => {
        if (disableButton()) {
          toast.error('Activity fields cant be empty in the Execution Plan');
          return
        }
        setShowLoader(true)
        reactFlowInstance.fitView({ padding: 0.1 });
        setTimeout(async () => {
          const viewportElement = document.querySelector('.react-flow__viewport');
          if (viewportElement instanceof HTMLElement) {
            const nodesBounds = getNodesBounds(reactFlowInstance.getNodes());
            const viewport = getViewportForBounds(
              nodesBounds,
              imageWidth,
              Infinity,
              0.01,
              2,
              10
            );

            const dataUrl = await toPng(viewportElement, {
              backgroundColor: '#FAFAFA',
              width: imageWidth,
              height: imageHeight,
              style: {
                width: imageWidth.toString(),
                height: imageHeight.toString(),
                transform: `translate(${viewport.x}px, ${viewport.y}px) scale(0.2)`,
              },
            });

            // Convert dataUrl to Blob
            const blob = dataURLtoBlob(dataUrl);

            const file = new File([blob], 'canvasImage.png', { type: 'image/png' });

            // API call to upload the file
            handleFileChange(file)
          } else {
            console.error('Viewport element not found.');
          }
        }, 100); // Adjust delay as needed
      },
      buttonVariant: 'filled',

    },
    {
      buttonText: 'Cancel',
      buttonId: 'cancel',
      btnClassName: 'secondary__btn',
      handleClick: (): void => {
        navigate('/new-rule-advance', { state: { mode: location?.state?.mode, ruleId: location?.state?.ruleId ?? advanceRuleEngineFormRedux?.id } });
      },
      isDisabled: false,
      buttonVariant: 'outlined',
    }
  ]

  return (
    <>
      <DashboardLoader showLoader={showLoader} />
      <div className='advance_rule_engine_dndflow'>
        <Sidebar nodes={nodes} levelOfHeirarchy={levelOfHeirarchy} />
        <div className="reactflow-wrapper" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChangeCustom}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            edgeTypes={edgeTypes}
            proOptions={proOptions}
            nodesDraggable={false}
            panOnDrag={true}
            elementsSelectable={true}
            deleteKeyCode={null}
            minZoom={0.05}
          >
            <Controls style={{ marginBottom: '40px' }} />
          </ReactFlow>

          <div className='rule__engine__canvas__footer'>
            <CustomButtonGroup
              buttonsList={btnsList}
              buttonGroupClassName='button__group__footer'
            />
          </div>
        </div>
      </div>
      <>
        <CustomDialog 
          dialogConfig={
            {
              dialogClassName: 'trigger__point__deletion__warning',
              dialogTitle: 'Warning',
              dialogDescription: 'Deleting this \'Trigger Point\' will remove the entire rule, as it is the parent node. Do you want to continue?',
              dialogCancelTitle: 'No',
              dialogOkTitle: 'Yes',
            }
          } 
          handleSubmit={handleResetExecutionPlan}
          handleClose={()=>{setShowDialog(false)}}
          show={showDialog} 
        />
      </>
    </>
  )
}

const ExecutionPlan: React.FC = () => {
  const [levelOfHierarchy, setLevelOfHeirarchy] = useState('trigger');
  const { advanceRuleEngineFormRedux } = useAppSelector(getAdvanceRuleEngineReducer);
  const location = useLocation();
  const breadCrumbs = useMemo(() => createBreadCrumbProps({
    breadCrumbProps:
      [
        {
          objectType: 'link',
          id: 'advanceRuleEngineNewRule',
          linkBtnState: { mode: location?.state?.mode, ruleId: location?.state?.ruleId ?? advanceRuleEngineFormRedux?.id }
        },
        {
          objectType: 'text',
          id: 'text',
          text: 'Define Execution Plan'
        },
        {
          objectType: 'text',
          id: 'text',
          text: advanceRuleEngineFormRedux?.name
        },

      ]
  }), [advanceRuleEngineFormRedux])
  return (
    <>
      <ReactFlowProvider>
        <DnDProvider>
          <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CustomBreadCrumbs breadCrumbs={breadCrumbs} levelOfHeirarchy={levelOfHierarchy} customRuleEngine={true} />
            <DnDFlow setLevelOfHeirarchy={setLevelOfHeirarchy} levelOfHeirarchy={levelOfHierarchy} />
          </div>
        </DnDProvider>
      </ReactFlowProvider>
    </>
  )
}

export const checkValidInteger = (data) => {
  const integerRegex = /^-?\d+$/;
  return integerRegex.test(data)
}

export const checkValidDecimal = (data) => {
  const decimalRegex = /^-?\d+(\.\d+)?$/;
  return decimalRegex.test(data);
}

export default ExecutionPlan
